import type { SVGProps } from 'react';

export type IconName =
  | 'cart'
  | 'search'
  | 'star'
  | 'check'
  | 'chevron-down'
  | 'chevron-right'
  | 'close'
  | 'shield'
  | 'bolt'
  | 'trash'
  | 'plus'
  | 'minus'
  | 'discord'
  | 'sparkles';

const PATHS: Record<IconName, JSX.Element> = {
  cart: (
    <path
      d="M2.5 3h1.6l1.2 12.2a1.5 1.5 0 0 0 1.5 1.35h8.9a1.5 1.5 0 0 0 1.47-1.2l1.35-6.6H6M9 20.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  search: (
    <path
      d="M11 4a7 7 0 1 0 4.2 12.6L20 21m-2.8-9A6.2 6.2 0 1 1 11 5.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  star: <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.9 6.6 20l1-6.1L3.2 9.5l6.1-.9Z" fill="currentColor" />,
  check: (
    <path d="m4 12 5 5 11-11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'chevron-down': (
    <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'chevron-right': (
    <path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  close: (
    <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  ),
  shield: (
    <path
      d="M12 3 5 5.5v5.2c0 4.3 3 8.3 7 9.3 4-1 7-5 7-9.3V5.5L12 3Zm-1.2 11.4L8 11.6l1.2-1.2 1.6 1.6 3.4-3.5 1.2 1.2-4.6 4.7Z"
      fill="currentColor"
    />
  ),
  bolt: <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" fill="currentColor" />,
  trash: (
    <path
      d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-8 0 .8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4L18 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  plus: <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />,
  minus: <path d="M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />,
  discord: (
    <path
      d="M19.3 5.4A16 16 0 0 0 15.3 4l-.2.4a12 12 0 0 1 3.4 1.7c-3.9-1.9-8.3-1.9-12.2 0A12 12 0 0 1 9.7 4.4L9.5 4a16 16 0 0 0-4 1.4C2.7 9.6 2 13.7 2.3 17.8a16 16 0 0 0 4.9 2.5l.6-1a10 10 0 0 1-1.7-.8l.4-.3c3.3 1.5 6.9 1.5 10.1 0l.4.3a10 10 0 0 1-1.7.8l.6 1a16 16 0 0 0 4.9-2.5c.4-4.7-.6-8.8-3-12.4ZM9 15.3c-1 0-1.7-.9-1.7-2s.8-2 1.7-2 1.7.9 1.7 2-.7 2-1.7 2Zm6 0c-1 0-1.7-.9-1.7-2s.8-2 1.7-2 1.7.9 1.7 2-.7 2-1.7 2Z"
      fill="currentColor"
    />
  ),
  sparkles: (
    <path
      d="M12 3l1.4 3.6L17 8l-3.6 1.4L12 13l-1.4-3.6L7 8l3.6-1.4L12 3Zm6 9 .8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2ZM5 13l.7 1.8L7.5 15l-1.8.7L5 17.5l-.7-1.8L2.5 15l1.8-.5L5 13Z"
      fill="currentColor"
    />
  ),
};

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
  title?: string;
}

export function Icon({ name, size = 20, title, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
